// @ts-nocheck
/**
 * ImageComparison Web Component
 *
 * Expects the full DOM to already exist in HTML (no innerHTML render).
 * Styles live in a separate CSS file.
 * No shadow root.
 *
 * Public API:
 *   setPosition(percent: number) → moves the divider
 *   updateImages(data: Object)   → swaps image src/link per JSON schema
 *
 * Required DOM structure inside <image-comparison>:
 *   .ic-root
 *     a.ic-link--left  > .ic-clip--left > img.ic-img--left
 *     a.ic-link--right > img.ic-img--right
 *     .ic-divider      > .ic-line + .ic-handle
 */

class ComparisonSliderComponent extends HTMLElement {

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  constructor() {
    super();
    this._position = 65;
    this._initialPosition = 65;
    this._isDragging = false;
    this._dragMoved = false;
    this._pointerStartX = null;
    this._hasAnimated = false;
    this._isInGrid = false;
    this._observer = null;

    // Pre-bind for add/remove symmetry
    this._boundPointerMove = this._onPointerMove.bind(this);
    this._boundPointerUp = this._onPointerUp.bind(this);
    this._boundTouchMove = this._onTouchMove.bind(this);
    this._boundTouchEnd = this._onTouchEnd.bind(this);
    this.isDropDownSection = false;
  }

  connectedCallback() {
    this.isDropDownSection = this.closest('.dropdown-image-container') != null;
    this._isInGrid = this.closest('.in-grid-image-comparison') != null;
    this._queryElements();
    if (this.isDropDownSection) {
      this._ensureDropdownAnchors();
      this._queryElements(); // re-query after wrapping elements
    }
    this._readInitialPosition();
    // Sync clip paths and click zones immediately — before intro animation / first tap.
    // Without this, the left anchor covers the full area and blocks the right link on touch devices.
    this._applyPosition(this._position);
    this._attachEventListeners();
    this._setupIntersectionObserver();
    if (this.isDropDownSection) {
      this._syncDropdownLinks();
    }
  }

  disconnectedCallback() {
    this._removeGlobalListeners();
    this._teardownIntersectionObserver();
  }

  _ensureDropdownAnchors() {
    // Only for dropdown-image-container slider: wrap left overlay and right image in anchors,
    // because the liquid markup here doesn't include .ic-link--left/.ic-link--right.
    const root = this.querySelector('.ic-root');
    if (!root) return;

    const clipLeft = root.querySelector('.ic-clip--left');
    const imgRight = root.querySelector('.ic-img--right');
    if (!clipLeft || !imgRight) return;

    if (!root.querySelector('a.ic-link--left')) {
      const leftLink = document.createElement('a');
      leftLink.className = 'ic-link--left';
      leftLink.href = '#';
      root.insertBefore(leftLink, clipLeft);
      leftLink.appendChild(clipLeft);
    }

    if (!root.querySelector('a.ic-link--right')) {
      const rightLink = document.createElement('a');
      rightLink.className = 'ic-link--right';
      rightLink.href = '#';
      root.insertBefore(rightLink, imgRight);
      rightLink.appendChild(imgRight);
    }
  }

  _syncDropdownLinks() {
    if (!this.isDropDownSection) return;
    if (!this._linkLeft || !this._linkRight) return;

    const scope = this.offsetParent;
    if (!scope) return;

    const leftActive = scope.querySelector('.left-dropdown-menu .collection-compare-button.is-active');
    const rightActive = scope.querySelector('.right-dropdown-menu .collection-compare-button.is-active');

    const leftHref = leftActive?.getAttribute('data-collection-url') || '#';
    const rightHref = rightActive?.getAttribute('data-collection-url') || '#';

    this._linkLeft.href = leftHref;
    this._linkRight.href = rightHref;

    // Keep in sync when dropdown selections change (delegated, only once)
    if (!scope.dataset.dropdownCompareLinkListenerAttached) {
      scope.dataset.dropdownCompareLinkListenerAttached = 'true';
      scope.addEventListener('click', (e) => {
        if (!e.target.closest('.collection-compare-button')) return;
        // Defer so `.is-active` class updates first
        setTimeout(() => this._syncDropdownLinks(), 0);
      });

      // Also listen to programmatic updates from the dropdown section script
      scope.addEventListener('dropdown-compare:change', () => {
        this._syncDropdownLinks();
      });
    }
  }

  // ─── Intersection Observer (trigger intro on view) ───────────────────────

  _setupIntersectionObserver() {
    if (this._hasAnimated) return;

    // Fallback for older browsers: run immediately
    if (typeof window.IntersectionObserver !== 'function') {
      this._runIntroAnimation();
      return;
    }

    this._observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !this._hasAnimated) {
          this._runIntroAnimation();
        }
      });
    }, {
      threshold: 0.25, // start when at least 25% is visible
    });

    this._observer.observe(this);
  }

  _teardownIntersectionObserver() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
  }

  // ─── Intro Animation ───────────────────────────────────────────────────────

  _runIntroAnimation() {
    if (this._hasAnimated) return;
    if (!this._divider || !this._clipLeft || !this._handle) return;

    // In-grid: skip intro — moving clip/divider breaks the first tap on image links.
    if (this._isInGrid) {
      this._hasAnimated = true;
      this._teardownIntersectionObserver();
      return;
    }

    this._hasAnimated = true;
    this._teardownIntersectionObserver();

    const DURATION = 5000; // ms total

    const keyframes = [
      { t: 0.00, p: 0 },
      { t: 0.25, p: 70 },
      { t: 0.55, p: 35 },
      { t: 0.75, p: 50 },
      { t: 1.00, p: 50 },
    ];

    // Only dropdown comparison sliders use the alternate ending positions.
    if (this.isDropDownSection) {
      keyframes[3] = { t: 0.75, p: 57 };
      keyframes[4] = { t: 1.00, p: 50 };
    }

    function easeInOut(t) {
      return t <= 0 ? 0 : t >= 1 ? 1 : t < 0.5
        ? 2 * t * t
        : 1 - Math.pow(-2 * t + 2, 2) / 2;
    }

    function lerp(a, b, t) {
      return a + (b - a) * t;
    }

    function getPosition(progress) {
      if (progress <= keyframes[0].t) return keyframes[0].p;
      for (let i = 1; i < keyframes.length; i++) {
        const prev = keyframes[i - 1];
        const curr = keyframes[i];
        const seg = curr.t - prev.t;
        if (progress <= curr.t && seg > 0) {
          const local = (progress - prev.t) / seg;
          return lerp(prev.p, curr.p, easeInOut(local));
        }
      }
      return keyframes[keyframes.length - 1].p;
    }

    this._applyPosition(keyframes[0].p);
    this.classList.add('is-animating');

    const startTime = performance.now();

    const tick = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / DURATION, 1);
      const pos = getPosition(progress);

      this._applyPosition(pos);

      if (progress < 1) {
        requestAnimationFrame(tick);
      } else {
        this.classList.remove('is-animating');
      }
    };

    requestAnimationFrame(tick);
  }

  // ─── Public API ────────────────────────────────────────────────────────────

  /**
   * Programmatically move the divider.
   * @param {number} percent  0 – 100
   */
  setPosition(percent) {
    this._applyPosition(this._clamp(percent, 0, 100));
  }

  /**
   * Reset divider back to the initial position read on connect.
   * Useful when surrounding UI swaps images/tabs.
   */
  resetPosition() {
    // this._applyPosition(this._initialPosition);
    this._hasAnimated = false;        // allow animation to run again
    this._runIntroAnimation();        // replay with current pos as start
  }

  /**
   * Swap images and links without touching the DOM structure.
   * @param {Object} data  { "slide-1": { src, alt, width, height, srcset, link },
   *                         "slide-2": { ... } }
   */
  updateImages(data) {
    this._applyImageData(data);
  }

  // ─── DOM Query ─────────────────────────────────────────────────────────────

  _queryElements() {
    this._root = this.querySelector('.ic-root');
    this._divider = this.querySelector('.ic-divider');
    this._clipLeft = this.querySelector('.ic-clip--left');
    this._handle = this.querySelector('.ic-handle');
    this._linkLeft = this.querySelector('.ic-link--left');
    this._linkRight = this.querySelector('.ic-link--right');
    this._imgLeft = this.querySelector('.ic-img--left');
    this._imgRight = this.querySelector('.ic-img--right');
  }

  // ─── Read initial position from existing inline styles (if any) ────────────

  // _readInitialPosition() {
  //   // Author may have pre-set left% on .ic-divider in the liquid template
  //   const dividerLeft = this._divider && this._divider.style.left;
  //   if (dividerLeft) {
  //     const parsed = parseFloat(dividerLeft);
  //     if (!isNaN(parsed)) {
  //       this._position = this._clamp(parsed, 0, 100);
  //       this._initialPosition = this._position;
  //       return;
  //     }
  //   }
  //   this._position = 80;
  //   this._initialPosition = 80;
  // }
  _readInitialPosition() {
    // Read from a data attribute so it's immune to animation side-effects
    const attr = this.getAttribute('data-initial-position');
    if (attr !== null) {
      const parsed = parseFloat(attr);
      if (!isNaN(parsed)) {
        this._position = this._clamp(parsed, 0, 100);
        this._initialPosition = this._position;
        return;
      }
    }

    // Fall back to inline style only if set before any animation runs
    const dividerLeft = this._divider && this._divider.style.left;
    if (dividerLeft) {
      const parsed = parseFloat(dividerLeft);
      if (!isNaN(parsed)) {
        this._position = this._clamp(parsed, 0, 100);
        this._initialPosition = this._position;
        return;
      }
    }

    this._position = 80;
    this._initialPosition = 80;
  }

  // ─── Event Wiring ──────────────────────────────────────────────────────────

  _attachEventListeners() {
    // Handle only – drag starts exclusively here
    this._handle.addEventListener('pointerdown', this._onPointerDown.bind(this));
    this._handle.addEventListener('touchstart', this._onTouchStart.bind(this), { passive: false });
  }

  _removeGlobalListeners() {
    document.removeEventListener('pointermove', this._boundPointerMove);
    document.removeEventListener('pointerup', this._boundPointerUp);
    document.removeEventListener('touchmove', this._boundTouchMove);
    document.removeEventListener('touchend', this._boundTouchEnd);
  }

  // ─── Pointer Events (mouse / stylus) ───────────────────────────────────────

  _onPointerDown(e) {
    if (e.pointerType === 'touch') return; // touch handled separately
    e.preventDefault();
    this._pointerStartX = e.clientX;
    this._startDrag();
    document.addEventListener('pointermove', this._boundPointerMove);
    document.addEventListener('pointerup', this._boundPointerUp);
  }

  _onPointerMove(e) {
    if (!this._isDragging) return;
    if (!this._dragMoved && this._pointerStartX != null) {
      if (Math.abs(e.clientX - this._pointerStartX) > 3) this._dragMoved = true;
    }
    this._applyPosition(this._positionFromClientX(e.clientX));
  }

  _onPointerUp() {
    this._finishDrag();
    document.removeEventListener('pointermove', this._boundPointerMove);
    document.removeEventListener('pointerup', this._boundPointerUp);
  }

  // ─── Touch Events (iOS / Android) ─────────────────────────────────────────

  _onTouchStart(e) {
    e.preventDefault(); // block page scroll while on the handle
    this._pointerStartX = e.touches[0]?.clientX ?? null;
    this._startDrag();
    document.addEventListener('touchmove', this._boundTouchMove, { passive: false });
    document.addEventListener('touchend', this._boundTouchEnd);
  }

  _onTouchMove(e) {
    if (!this._isDragging) return;
    e.preventDefault();
    const clientX = e.touches[0]?.clientX;
    if (!this._dragMoved && this._pointerStartX != null && clientX != null) {
      if (Math.abs(clientX - this._pointerStartX) > 3) this._dragMoved = true;
    }
    if (clientX != null) this._applyPosition(this._positionFromClientX(clientX));
  }

  _onTouchEnd() {
    this._finishDrag();
    document.removeEventListener('touchmove', this._boundTouchMove);
    document.removeEventListener('touchend', this._boundTouchEnd);
  }

  // ─── Drag State ────────────────────────────────────────────────────────────

  _startDrag() {
    this._isDragging = true;
    this._dragMoved = false;
    this._root.style.cursor = 'grabbing';
  }

  _finishDrag() {
    const moved = this._dragMoved;
    this._isDragging = false;
    this._dragMoved = false;
    this._pointerStartX = null;
    this._root.style.cursor = '';
    // Block only the synthetic click from releasing over a link after a real drag.
    // Do NOT use a persistent flag — that blocked the next intentional image click.
    if (moved) this._blockGhostClickOnce();
  }

  _blockGhostClickOnce() {
    const cleanup = () => {
      this.removeEventListener('click', block, true);
      clearTimeout(timer);
    };
    const block = (e) => {
      if (!e.target.closest('.ic-link--left, .ic-link--right')) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      cleanup();
    };
    const timer = setTimeout(cleanup, 400);
    this.addEventListener('click', block, true);
  }

  // ─── Position Math ─────────────────────────────────────────────────────────

  _positionFromClientX(clientX) {
    const rect = this._root.getBoundingClientRect();
    return this._clamp(((clientX - rect.left) / rect.width) * 100, 0, 100);
  }

  _clamp(val, min, max) {
    return Math.min(Math.max(val, min), max);
  }

  // ─── Apply Position ────────────────────────────────────────────────────────

  /**
   * Single source of truth. Updates divider position, clip, ARIA, and click zones.
   * @param {number} percent  0 – 100
   */
  _applyPosition(percent) {
    this._position = percent;
    const right = (100 - percent).toFixed(4);
    const pct = percent.toFixed(4);

    // 1. Move the divider bar
    this._divider.style.left = `${pct}%`;

    // 2. Clip the left overlay — reveal only the left portion.
    //    clip-path: inset(top right bottom left)
    this._clipLeft.style.clipPath = `inset(0 ${right}% 0 0)`;

    // 3. ARIA
    this._handle.setAttribute('aria-valuenow', Math.round(percent));

    // 4. Sync click zones
    this._updateClickZones(percent);
  }

  // ─── Click Zones ───────────────────────────────────────────────────────────

  /**
   * The left anchor's pointer-events area is clipped to match its visible region
   * so clicks only register where the image is actually visible.
   * The right anchor sits beneath and covers the rest.
   */
  _updateClickZones(percent) {
    if (!this._linkLeft) return;
    const right = (100 - percent).toFixed(4);
    this._linkLeft.style.clipPath = `inset(0 ${right}% 0 0)`;
    this._linkLeft.style.pointerEvents = percent > 0 ? 'auto' : 'none';
  }

  // ─── Image Data Update ─────────────────────────────────────────────────────

  _applyImageData(data) {
    const s1 = data['slide-1'];
    const s2 = data['slide-2'];

    if (s1) {
      this._setImageAttrs(this._imgLeft, s1);
      this._setLinkHref(this._linkLeft, s1.link);
    }

    if (s2) {
      this._setImageAttrs(this._imgRight, s2);
      this._setLinkHref(this._linkRight, s2.link);
    }

    // Re-clip after images change
    this._applyPosition(this._position);
  }

  // REPLACE the existing _setImageAttrs method with this:
  _setImageAttrs(img, data) {
    if (!img) return;
    if (data.src !== undefined) {
      img.src = data.src;
      img.removeAttribute('data-src'); // clean up the no-fetch placeholder
    }
    if (data.alt !== undefined) img.alt = data.alt;
    if (data.width !== undefined) img.width = data.width;
    if (data.height !== undefined) img.height = data.height;
    if (data.srcset) img.srcset = data.srcset;
    else img.removeAttribute('srcset');
  }

  _setLinkHref(anchor, href) {
    if (!anchor) return;
    if (href) anchor.href = href;
    else anchor.removeAttribute('href');
  }
}

// ─── Register ──────────────────────────────────────────────────────────────

if (!customElements.get('comparison-slider-component')) {
  customElements.define('comparison-slider-component', ComparisonSliderComponent);
}


window.ComparisonSliderComponent = ComparisonSliderComponent;

